import { useEffect } from 'react';
import { onIdTokenChanged, getRedirectResult } from 'firebase/auth';
import type { User as FirebaseAuthUser } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '@config/firebase';
import { useAppDispatch } from '@store/hooks';
import { setUser, setUserProfile, setLoading } from '@store/slices/authSlice';
import api from '@services/api';
import { APP_ROUTES } from '@config/routes';
import { teamService } from '@services/teamService';
import type { User } from '@/types';

export const useAuth = () => {
  const dispatch = useAppDispatch();
  const log = (...args: unknown[]) => {
    if (import.meta.env.DEV) {
      console.log(...args);
    }
  };

  useEffect(() => {
    let redirectHandled = false;
    const inviteTokenFromUrl = new URLSearchParams(window.location.search).get('teamInvite');
    if (inviteTokenFromUrl) {
      sessionStorage.setItem('teamInviteToken', inviteTokenFromUrl);
    }
    const writeRedirectDebug = (status: 'success' | 'no-result' | 'error', extra = {}) => {
      const payload = {
        status,
        timestamp: new Date().toISOString(),
        href: window.location.href,
        ...extra,
      };

      try {
        sessionStorage.setItem('auth:lastRedirectDebug', JSON.stringify(payload));
      } catch (storageError) {
        console.error('[useAuth] Failed to persist redirect debug payload:', storageError);
      }

      if (status === 'error') {
        console.error('[useAuth] Redirect debug:', payload);
        return;
      }

      if (status === 'no-result') {
        console.warn('[useAuth] Redirect debug:', payload);
        return;
      }

      log('[useAuth] Redirect debug:', payload);
    };

    // CRITICAL: Handle redirect result FIRST before setting up listeners
    const handleRedirect = async () => {
      if (redirectHandled) {
        log('[useAuth] Redirect already handled, skipping...');
        return;
      }

      // Debug: Check URL for OAuth params
      const currentUrl = window.location.href;
      const hasOAuthParams = currentUrl.includes('state=') || currentUrl.includes('code=');
      log('[useAuth] Current URL:', currentUrl);
      log('[useAuth] Has OAuth params:', hasOAuthParams);

      // Check sessionStorage for pending redirect (Firebase stores it here)
      const pendingRedirect = sessionStorage.getItem('firebase:pendingRedirect');
      log('[useAuth] Pending redirect in sessionStorage:', pendingRedirect);

      try {
        log('[useAuth] Checking for redirect result...');
        const result = await getRedirectResult(auth);

        if (result) {
          redirectHandled = true;
          log('[useAuth] ✅ Redirect result found:', result.user.email);
          writeRedirectDebug('success', {
            uid: result.user.uid,
            email: result.user.email,
            providerIds: result.user.providerData.map(provider => provider.providerId),
          });
          const { user } = result;

          // Transient flag: mark that a Google redirect sign-in occurred
          sessionStorage.setItem('googleAuth', 'true');

          // Do NOT create a backend profile yet — defer until user accepts Terms.
          const isNewUser =
            result.user.metadata.creationTime === result.user.metadata.lastSignInTime;
          log('[useAuth] Redirect result isNewUser:', isNewUser);

          if (isNewUser) {
            // Save a short-lived prefill so Register page can open Terms modal and prefill email
            sessionStorage.setItem('registrationMode', 'google');
            sessionStorage.setItem('registrationPrefillEmail', user.email || '');

            log('[useAuth] Routing new redirected Google user to Register to accept Terms');
            // Keep the Firebase session active and navigate to the Register flow
            window.location.assign(APP_ROUTES.REGISTER);
            return;
          }

          // Existing user on redirect: verify Firestore profile exists before proceeding.
          // Firebase Auth may mark a user as "existing" even when their Firestore document
          // was deleted (incomplete account deletion). Routing to dashboard without a profile
          // causes ProtectedRoute and useAuth listeners to race.
          const existingDoc = await getDoc(doc(db, 'users', result.user.uid));
          if (existingDoc.exists()) {
            try {
              const fresh = await (await import('@services/userService')).userService.getProfile();
              dispatch(setUserProfile(fresh));
            } catch (err) {
              console.error(
                '[useAuth] Failed to fetch profile after redirect for existing user:',
                err
              );
              // Best-effort: data is in Firestore but API call failed — let onAuthStateChanged
              // handle profile hydration via handleUserChange.
            }
          } else {
            // Firebase Auth exists but Firestore profile is missing — same as a new user.
            // Route through register so they can complete onboarding.
            log(
              '[useAuth] Redirect: existing Firebase Auth user has no Firestore profile — routing to Register'
            );
            sessionStorage.setItem('registrationMode', 'google');
            sessionStorage.setItem('registrationPrefillEmail', result.user.email || '');
            window.location.assign(APP_ROUTES.REGISTER);
            return;
          }

          log('[useAuth] ✅ Redirect handling complete');
        } else {
          log('[useAuth] No redirect result (normal page load)');
          if (hasOAuthParams || pendingRedirect) {
            writeRedirectDebug('no-result', {
              hasOAuthParams,
              pendingRedirect,
            });
          }
        }
      } catch (error) {
        console.error('[useAuth] ❌ Error handling redirect:', error);
        const code = (error as { code?: string } | undefined)?.code;
        const message = (error as Error | undefined)?.message;
        writeRedirectDebug('error', {
          code,
          message,
          hasOAuthParams,
          pendingRedirect,
        });
        console.error('[useAuth] Error code:', code);
        console.error('[useAuth] Error message:', message);
      }
    };

    // Handle redirect first, then set up listener
    handleRedirect().then(() => {
      log('[useAuth] Setting up onAuthStateChanged listener...');
    });

    const handleUserChange = async (firebaseUser: FirebaseAuthUser | null) => {
      // Keep the full Firebase user in the auth slice (serializable profile is stored separately)
      dispatch(setUser(firebaseUser ?? null));

      // Wrap in try/finally so dispatch(setLoading(false)) always runs —
      // even when early‑return branches exit before the end of the function.
      try {
        if (firebaseUser) {
          // Fetch user profile from Firestore
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

            if (!userDoc.exists()) {
              // No backend profile exists for this Firebase Auth user. Likely a
              // social sign-in that hasn't completed Terms/onboarding. Route to Register.
              try {
                log(
                  '[useAuth] No backend profile found for authenticated user — considering routing to Register'
                );

                // Guard: prevent infinite loop if already on Register page
                const alreadyOnRegister = window.location.pathname === APP_ROUTES.REGISTER;
                const lastRegisterRedirect = Number(
                  sessionStorage.getItem('lastRegisterRedirect') || '0'
                );
                const now = Date.now();
                const REDIRECT_THROTTLE_MS = 5000;

                // Store email and set Google flag for RegisterPage to detect
                sessionStorage.setItem('registrationPrefillEmail', firebaseUser.email || '');

                // Detect if this is a Google sign-in (check provider data)
                const isGoogleUser = firebaseUser.providerData.some(
                  p => p.providerId === 'google.com'
                );
                if (isGoogleUser) {
                  sessionStorage.setItem('registrationMode', 'google');
                  log(
                    '[useAuth] Detected Google user without profile — setting registrationMode flag'
                  );
                }

                if (!alreadyOnRegister && now - lastRegisterRedirect > REDIRECT_THROTTLE_MS) {
                  sessionStorage.setItem('lastRegisterRedirect', String(now));
                  log('[useAuth] Navigating to Register page');
                  window.location.assign(APP_ROUTES.REGISTER);
                  return; // stop further processing
                }
                if (alreadyOnRegister) {
                  log(
                    '[useAuth] Already on Register page — skipping navigation, letting RegisterPage handle it'
                  );
                  return; // stop further processing so RegisterPage can take over
                }
                console.warn('[useAuth] Skipping repeated Register redirect (throttled)');
                return;
              } catch (e) {
                console.error('[useAuth] Failed to route new user to Register:', e);
              }
            }

            // At this point the backend profile exists — safe to read it
            const userData = userDoc.data() as Record<string, any>;

            // Sync emailVerified status from Firebase Auth to Firestore if different
            if (userData.emailVerified !== firebaseUser.emailVerified) {
              log(
                `[useAuth] Syncing emailVerified status: ${userData.emailVerified} → ${firebaseUser.emailVerified}`
              );
              await updateDoc(doc(db, 'users', firebaseUser.uid), {
                emailVerified: firebaseUser.emailVerified,
                updatedAt: new Date(),
              });
            }

            // Convert ALL Firebase Timestamps to serializable strings
            const serializableUserData: Partial<User> = {
              ...userData,
              emailVerified: firebaseUser.emailVerified, // Use Firebase Auth as source of truth
              // Fallback to Firebase Auth photoURL if Firestore doesn't have it (fixes Gmail profile pic)
              photoURL: userData.photoURL || firebaseUser.photoURL || undefined,
              createdAt: userData.createdAt?.toDate?.()?.toISOString() || userData.createdAt,
              updatedAt: userData.updatedAt?.toDate?.()?.toISOString() || userData.updatedAt,
              lastLoginAt: userData.lastLoginAt?.toDate?.()?.toISOString() || userData.lastLoginAt,
              nextBillingDate:
                userData.nextBillingDate?.toDate?.()?.toISOString() || userData.nextBillingDate,
            };

            // Extract commonly used fields and role in a type-safe way
            const role = (serializableUserData as { role?: string } | undefined)?.role;
            const email = (serializableUserData as { email?: string } | undefined)?.email;
            const displayName = (serializableUserData as { displayName?: string } | undefined)
              ?.displayName;

            log('[useAuth] Setting userProfile with role:', role);
            log('[useAuth] Full userProfile data:', { email, role, displayName });

            dispatch(setUserProfile(serializableUserData as unknown as User));

            const inviteToken =
              new URLSearchParams(window.location.search).get('teamInvite') ||
              sessionStorage.getItem('teamInviteToken');
            teamService
              .claimInvitations(inviteToken || undefined)
              .then(memberships => {
                if (memberships.length > 0) sessionStorage.removeItem('teamInviteToken');
              })
              .catch(error => {
                console.error('[useAuth] Failed to claim team invitation:', error);
              });

            // Detect incomplete onboarding and set/clear a session flag to show a "Resume Onboarding" prompt
            try {
              const createdAtMs = serializableUserData.createdAt
                ? new Date(serializableUserData.createdAt).getTime()
                : 0;
              const isRecentlyCreated = createdAtMs && Date.now() - createdAtMs < 60_000; // 1 minute

              const missingProfileFields =
                !serializableUserData.businessName || !serializableUserData.industry;
              const hasPendingPlan = (serializableUserData.subscriptionPlan || '').startsWith(
                'pending_'
              );

              // Skip onboarding check for admin users
              const isAdmin = role === 'admin';

              // Skip onboarding check if just completed payment (prevents loop)
              const justCompletedPayment = sessionStorage.getItem('paymentCompleted') === 'true';
              const hasWelcomeParam = window.location.search.includes('welcome=true');
              const hasSessionId = window.location.search.includes('session_id=');
              const isOnProcessingPage = window.location.pathname === '/processing';

              // If the account is not brand-new and has incomplete profile, redirect to plan selection
              if (
                !isRecentlyCreated &&
                !isAdmin &&
                !justCompletedPayment &&
                !hasWelcomeParam &&
                !hasSessionId &&
                !isOnProcessingPage &&
                (missingProfileFields || hasPendingPlan)
              ) {
                log(
                  '[useAuth] Incomplete profile detected — considering redirect to Plan Selection'
                );

                // Add guards to avoid repeatedly navigating to the same route (can happen during HMR or auth re-checks)
                const alreadyOnPlanSelection =
                  window.location.pathname === APP_ROUTES.PLAN_SELECTION;
                const lastRedirect = Number(
                  sessionStorage.getItem('lastOnboardingRedirect') || '0'
                );
                const now = Date.now();
                const REDIRECT_THROTTLE_MS = 5000; // allow redirect at most once per 5s

                // Always set resume flag for UI, but only perform a hard navigation if not already on the page
                sessionStorage.setItem('resumeOnboarding', 'true');

                if (!alreadyOnPlanSelection && now - lastRedirect > REDIRECT_THROTTLE_MS) {
                  try {
                    sessionStorage.setItem('lastOnboardingRedirect', String(now));
                    log('[useAuth] Redirecting to plan selection (throttle OK)');
                    window.location.assign(APP_ROUTES.PLAN_SELECTION);
                    return; // stop further processing
                  } catch (e) {
                    console.error('[useAuth] Failed to navigate to Plan Selection:', e);
                  }
                } else if (alreadyOnPlanSelection) {
                  log('[useAuth] Already on Plan Selection — skipping navigation');
                } else {
                  console.warn('[useAuth] Skipping repeated plan selection redirect (throttled)');
                }
              } else if (sessionStorage.getItem('resumeOnboarding')) {
                // Onboarding appears complete (plan active / profile fields present) — clear the resume flag if present
                log(
                  '[useAuth] Onboarding complete or payment detected — clearing resumeOnboarding flag'
                );
                sessionStorage.removeItem('resumeOnboarding');
              }
            } catch (resumeErr) {
              console.error('[useAuth] Failed to evaluate resume onboarding condition:', resumeErr);
            }

            // Auto-sync subscription if there is an obvious mismatch
            try {
              const { uid } = firebaseUser;
              const subsPlan = serializableUserData.subscriptionPlan as string | undefined;
              const hasStripe = !!serializableUserData.stripeSubscriptionId;
              const activeStripe = serializableUserData.subscriptionStatus === 'active';
              const hasMRR =
                typeof serializableUserData.mrr === 'number' && serializableUserData.mrr > 0;
              const needsSync =
                (subsPlan === 'free' && (hasStripe || activeStripe || hasMRR)) ||
                (subsPlan || '').startsWith('pending_');

              const syncStateKey = `subscriptionSyncState:${uid}`;
              const syncRetryAtKey = `subscriptionSyncRetryAt:${uid}`;
              const syncStateSignature = [
                subsPlan || 'none',
                hasStripe ? 'stripe' : 'no-stripe',
                activeStripe ? 'active' : 'inactive',
                hasMRR ? 'mrr' : 'no-mrr',
              ].join(':');
              const alreadyHandledState =
                sessionStorage.getItem(syncStateKey) === syncStateSignature;
              const retryAt = Number(sessionStorage.getItem(syncRetryAtKey) || '0');
              const isRetryCoolingDown = retryAt > Date.now();

              if (needsSync && !alreadyHandledState && !isRetryCoolingDown) {
                log('[useAuth] Detected subscription mismatch - attempting background sync');

                const isOnPlanSelection = window.location.pathname === '/plan-selection';

                // NOTE: Do NOT skip sync on plan-selection when there's a pending_ plan.
                // After a Stripe checkout the user may be redirected back to plan-selection
                // (e.g. wrong success_url port on dev). The sync must run to resolve
                // pending_<plan> → <plan> so they can proceed to the dashboard.

                // Use singleton onboarding toast to avoid duplicate toasts
                const { toastSingleton, TOAST_KEYS } = await import('@utils/toastUtils');
                const loadingToastTimer = window.setTimeout(() => {
                  toastSingleton.loading(
                    TOAST_KEYS.ONBOARD,
                    'Syncing billing info in the background...'
                  );
                }, 800);

                try {
                  // Use existing API helper
                  const res = await api.post('/sync/sync-subscription');
                  window.clearTimeout(loadingToastTimer);

                  // Refresh Firestore-based profile from backend
                  const fresh = await (
                    await import('@services/userService')
                  ).userService.getProfile();
                  dispatch(setUserProfile(fresh));
                  sessionStorage.setItem(syncStateKey, syncStateSignature);
                  sessionStorage.setItem(`subscriptionSynced:${uid}`, 'true');
                  sessionStorage.removeItem(syncRetryAtKey);

                  // If sync resolved a pending plan or payment, clear the resume flag so the dashboard won't prompt again
                  if (sessionStorage.getItem('resumeOnboarding')) {
                    log('[useAuth] Clearing resumeOnboarding after subscription sync');
                    sessionStorage.removeItem('resumeOnboarding');
                  }

                  // Clear payment completion flag after successful sync
                  if (sessionStorage.getItem('paymentCompleted')) {
                    log('[useAuth] Clearing paymentCompleted flag after successful sync');
                    sessionStorage.removeItem('paymentCompleted');
                  }

                  // Dismiss the loading toast silently - dashboard will show welcome message
                  toastSingleton.dismiss(TOAST_KEYS.ONBOARD);
                  log('[useAuth] Subscription sync complete', res);
                } catch (err) {
                  window.clearTimeout(loadingToastTimer);
                  const errorMessage = err instanceof Error ? err.message : String(err);
                  const isNoCustomerError = errorMessage.includes('No Stripe customer found');
                  const isNoSubscriptionError = errorMessage.includes(
                    'No active subscription found'
                  );
                  const canMemoizeNoOpState = subsPlan === 'free';

                  // Silently ignore these errors — both are expected for free users:
                  // - "No Stripe customer found": user has never had a paid subscription.
                  // - "No active subscription found": user has a Stripe customer but no active paid plan.
                  if (isNoCustomerError || isNoSubscriptionError) {
                    if (canMemoizeNoOpState) {
                      sessionStorage.setItem(syncStateKey, syncStateSignature);
                      sessionStorage.removeItem(syncRetryAtKey);
                    } else {
                      sessionStorage.setItem(syncRetryAtKey, String(Date.now() + 2 * 60 * 1000));
                    }
                    log(
                      '[useAuth] Silently ignoring sync error — no active Stripe subscription (expected for free users)'
                    );
                    toastSingleton.dismiss(TOAST_KEYS.ONBOARD);
                  } else if (!isOnPlanSelection) {
                    sessionStorage.setItem(syncRetryAtKey, String(Date.now() + 2 * 60 * 1000));
                    // Show error only for unexpected sync failures when NOT in onboarding
                    toastSingleton.error(
                      TOAST_KEYS.ONBOARD,
                      "We couldn't update your billing info right now. Please try again later or contact support if this continues."
                    );
                    console.error('[useAuth] Auto-sync failed:', err);
                  } else {
                    sessionStorage.setItem(syncRetryAtKey, String(Date.now() + 2 * 60 * 1000));
                    toastSingleton.dismiss(TOAST_KEYS.ONBOARD);
                    log('[useAuth] Suppressing sync error during plan selection:', err);
                  }
                }
              }
            } catch (syncErr) {
              console.error('[useAuth] Auto-sync failed (outer):', syncErr);
            }
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            console.error('Error fetching user profile:', msg);

            // Firestore WebChannel can occasionally fail in local/dev networks
            // (ERR_CONNECTION_CLOSED / code=unavailable). Fall back to backend
            // profile API so auth state can still hydrate.
            const isFirestoreConnectivityError =
              msg.includes('offline') ||
              msg.includes('unavailable') ||
              msg.includes('ERR_CONNECTION_CLOSED') ||
              msg.includes('network');

            if (isFirestoreConnectivityError) {
              try {
                log('[useAuth] Firestore unavailable — falling back to /api/users/profile');
                const fresh = await (
                  await import('@services/userService')
                ).userService.getProfile();
                dispatch(setUserProfile(fresh));
                log('[useAuth] Fallback profile hydration succeeded');
              } catch (fallbackErr) {
                const status = (fallbackErr as { response?: { status?: number } })?.response
                  ?.status;

                // If backend profile doesn't exist yet, route to Register like the
                // normal Firestore-missing branch does.
                if (status === 404) {
                  try {
                    const alreadyOnRegister = window.location.pathname === APP_ROUTES.REGISTER;
                    const lastRegisterRedirect = Number(
                      sessionStorage.getItem('lastRegisterRedirect') || '0'
                    );
                    const now = Date.now();
                    const REDIRECT_THROTTLE_MS = 5000;

                    sessionStorage.setItem('registrationPrefillEmail', firebaseUser.email || '');

                    const isGoogleUser = firebaseUser.providerData.some(
                      p => p.providerId === 'google.com'
                    );
                    if (isGoogleUser) {
                      sessionStorage.setItem('registrationMode', 'google');
                    }

                    if (!alreadyOnRegister && now - lastRegisterRedirect > REDIRECT_THROTTLE_MS) {
                      sessionStorage.setItem('lastRegisterRedirect', String(now));
                      window.location.assign(APP_ROUTES.REGISTER);
                      return;
                    }
                  } catch (routeErr) {
                    console.error('[useAuth] Fallback 404 routing failed:', routeErr);
                  }
                }

                console.error('[useAuth] Fallback profile hydration failed:', fallbackErr);
              }
            }
          }
        } else {
          dispatch(setUserProfile(null));
        }
      } finally {
        dispatch(setLoading(false));
      }
    };

    // Use onIdTokenChanged instead of onAuthStateChanged to detect token
    // revocation from other sessions (e.g. password changed on mobile).
    const unsubscribe = onIdTokenChanged(auth, handleUserChange);

    // Proactive token refresh: every 10 minutes, force-refresh the ID token.
    // If the refresh token was revoked (password changed elsewhere), this will
    // fail and Firebase will sign the user out — triggering the listener above.
    const TOKEN_REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
    const refreshInterval = setInterval(async () => {
      const { currentUser } = auth;
      if (currentUser) {
        try {
          await currentUser.getIdToken(true);
        } catch (err) {
          log('[useAuth] Proactive token refresh failed — session likely revoked:', err);
          // Firebase SDK will auto-sign-out and trigger onIdTokenChanged
        }
      }
    }, TOKEN_REFRESH_INTERVAL_MS);

    // Re-validate auth when the browser tab regains focus (e.g. user switches
    // from mobile back to the web browser). Also refreshes the user profile
    // so subscription/plan changes made elsewhere are reflected immediately.
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && auth.currentUser) {
        try {
          await auth.currentUser.getIdToken(true);

          // Re-fetch profile so any external changes (subscription, settings) are synced
          const fresh = await (await import('@services/userService')).userService.getProfile();
          dispatch(setUserProfile(fresh));
        } catch (err) {
          log('[useAuth] Token refresh on tab focus failed — session likely revoked:', err);
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      unsubscribe();
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };

    // We intentionally run this effect once on mount — dispatch is stable from our hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
